import React, { useEffect, useRef } from 'react'
import { Button, CircularProgress } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import { CoachAvailabilityVideoCallSlot } from '../../useCoachAvailabilityDays'
import { CallBookingParticipantFragment } from '../../../videoCallBookingHeaderQueries.generated'
import useBookCall from './useBookCall'

interface BookCallButtonProps {
  disabled?: boolean
  onCloseDialog: () => void
  participant: CallBookingParticipantFragment
  selectedSlot: CoachAvailabilityVideoCallSlot | null
}

const BookCallButton = ({ disabled = false, onCloseDialog, participant, selectedSlot }: BookCallButtonProps) => {
  const { loading, success, bookCall } = useBookCall({
    participant,
    coachEmail: selectedSlot?.coachEmail ?? null,
    startTime: selectedSlot?.start.toJSDate() ?? null,
    availabilityCalendarEventID: selectedSlot?.availabilityCalendarEventID,
  })
  const prevSuccessRef = useRef(false)

  useEffect(() => {
    if (prevSuccessRef.current && !success) {
      onCloseDialog()
    }
    prevSuccessRef.current = success
  }, [success, onCloseDialog])

  const buttonStates = {
    loading: (
      <>
        <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
        Scheduling...
      </>
    ),
    success: (
      <>
        <CheckIcon style={{ marginRight: 8 }} />
        Scheduled
      </>
    ),
    default: 'Schedule call',
  }

  const getButtonContent = () => {
    if (loading) return buttonStates.loading
    if (success) return buttonStates.success
    return buttonStates.default
  }

  return (
    <Button
      color="primary"
      variant="contained"
      disabled={disabled || loading || success}
      onClick={bookCall}
      sx={{
        marginTop: (theme) => theme.spacing(2),
        alignSelf: 'center',
        fontWeight: 'bold',
        textTransform: 'uppercase',
      }}
    >
      {getButtonContent()}
    </Button>
  )
}

export default BookCallButton
